import { 
  Stepper, 
  Step, 
  StepLabel, 
  StepIcon, 
  StepConnector, 
  stepConnectorClasses,
  Box,
  Typography,
  useMediaQuery,
  Theme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useBrand } from '../../brands/BrandProvider';

// Estilização personalizada para o conector entre etapas
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        `linear-gradient(95deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        `linear-gradient(95deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

// Estilização personalizada para os ícones das etapas
const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage:
      `linear-gradient(136deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage:
      `linear-gradient(136deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
  }),
}));

// Componente personalizado para os ícones das etapas
function ColorlibStepIcon(props: {
  active: boolean;
  completed: boolean;
  icon: React.ReactNode;
}) {
  const { active, completed, icon } = props;

  const icons: { [index: string]: React.ReactElement } = {
    1: <InfoOutlinedIcon />,
    2: <PaymentOutlinedIcon />,
    3: <HowToRegOutlinedIcon />,
    4: <DesignServicesOutlinedIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }}>
      {completed ? <CheckCircleOutlineIcon /> : icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}

interface EventFormStepperProps {
  activeStep: number;
}

const EventFormStepper: React.FC<EventFormStepperProps> = ({ activeStep }) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const brand = useBrand();
  const steps = brand.copy.eventForm.steps;

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper 
        activeStep={activeStep} 
        alternativeLabel={!isMobile}
        connector={<ColorlibConnector />}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        sx={{ mb: 4 }}
      >
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel 
              StepIconComponent={(props) => 
                <ColorlibStepIcon 
                  active={props.active || false} 
                  completed={props.completed || false} 
                  icon={props.icon} 
                />
              }
            >
              <Typography variant="subtitle2" fontWeight={activeStep === index ? 'bold' : 'normal'}>
                {step.label}
              </Typography>
              {!isMobile && (
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              )}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default EventFormStepper;
